import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getCardShadowClass(type?: string) {
  if (type === "sm") return "shadow-[0_4px_12px_rgba(0,0,0,0.35),0_1px_3px_rgba(0,0,0,0.2)]";
  if (type === "md") return "shadow-[0_10px_25px_-3px_rgba(0,0,0,0.55),0_4px_8px_-2px_rgba(0,0,0,0.3)]";
  if (type === "xl") return "shadow-[0_24px_50px_rgba(0,0,0,0.75),0_10px_20px_rgba(0,0,0,0.4)]";
  if (type === "glow") return "shadow-[0_0_25px_var(--primary-500),0_10px_25px_rgba(0,0,0,0.5)]";
  return "shadow-[0_10px_25px_-3px_rgba(0,0,0,0.55),0_4px_8px_-2px_rgba(0,0,0,0.3)]";
}

export function isDocumentFullscreen(): boolean {
  try {
    const doc = document as any;
    return !!(
      doc.fullscreenElement ||
      doc.webkitFullscreenElement ||
      doc.mozFullScreenElement ||
      doc.msFullscreenElement
    );
  } catch {
    return false;
  }
}

export function safeRequestFullscreen() {
  try {
    if (typeof document === 'undefined') return;
    if (isDocumentFullscreen()) return;

    // Strict user gesture check: Modern browsers (Chrome, Edge, Safari) reject requestFullscreen()
    // with "API can only be initiated by a user gesture" if triggered outside a user click/touch event.
    if (typeof navigator !== 'undefined' && 'userActivation' in navigator) {
      if (!(navigator as any).userActivation?.isActive) {
        return;
      }
    }

    const docEl = document.documentElement as any;
    if (!docEl) return;

    const requestFS =
      docEl.requestFullscreen ||
      docEl.webkitRequestFullscreen ||
      docEl.webkitRequestFullScreen ||
      docEl.mozRequestFullScreen ||
      docEl.msRequestFullscreen;

    if (typeof requestFS === 'function') {
      const promise = requestFS.call(docEl);
      if (promise && typeof promise.catch === 'function') {
        promise.catch(() => {
          // Quietly ignore fullscreen permission or user gesture restrictions
        });
      }
    }
  } catch (_err) {
    // Ignore fullscreen errors silently
  }
}

// Scoped fullscreen restore helper without global aggressive hijacking
export function setupFullscreenRecoveryOnInteraction() {
  // Kept as safe no-op to preserve backwards compatibility without intrusive global listeners
}

import ahmetImg from '../assets/avatars/ahmet.jpg';
import alpImg from '../assets/avatars/alp.jpg';
import asliImg from '../assets/avatars/asli.jpg';
import canImg from '../assets/avatars/can.jpg';
import eceImg from '../assets/avatars/ece.jpg';
import elifImg from '../assets/avatars/elif.jpg';
import emreImg from '../assets/avatars/emre.jpg';
import fallbackFemaleImg from '../assets/avatars/fallback_female.jpg';
import fallbackMaleImg from '../assets/avatars/fallback_male.jpg';
import gizemImg from '../assets/avatars/gizem.jpg';
import mehmetImg from '../assets/avatars/mehmet.jpg';
import ozanImg from '../assets/avatars/ozan.jpg';
import selinImg from '../assets/avatars/selin.jpg';
import zeynepImg from '../assets/avatars/zeynep.jpg';

export function getAssetUrl(relativePath: string): string {
  const base = import.meta.env.BASE_URL || '/';
  const cleanBase = base.endsWith('/') ? base.slice(0, -1) : base;
  const cleanPath = relativePath.startsWith('/') ? relativePath : '/' + relativePath;
  return `${cleanBase}${cleanPath}`;
}

export const ALL_AVATAR_KEYS = [
  'ahmet', 'zeynep', 'can', 'elif', 'mehmet', 'ece',
  'ozan', 'asli', 'emre', 'gizem', 'alp', 'selin'
];

// Local bundled avatar maps with direct static imports
export const AVATAR_MAP: Record<string, string> = {
  ahmet: ahmetImg,
  alp: alpImg,
  asli: asliImg,
  can: canImg,
  ece: eceImg,
  elif: elifImg,
  emre: emreImg,
  fallback_female: fallbackFemaleImg,
  fallback_male: fallbackMaleImg,
  gizem: gizemImg,
  mehmet: mehmetImg,
  ozan: ozanImg,
  selin: selinImg,
  zeynep: zeynepImg,
};

export const REALISTIC_UNSPLASH_AVATARS: Record<string, string> = {
  ahmet: AVATAR_MAP['ahmet'],
  alp: AVATAR_MAP['alp'],
  asli: AVATAR_MAP['asli'],
  can: AVATAR_MAP['can'],
  ece: AVATAR_MAP['ece'],
  elif: AVATAR_MAP['elif'],
  emre: AVATAR_MAP['emre'],
  fallback_female: AVATAR_MAP['fallback_female'],
  fallback_male: AVATAR_MAP['fallback_male'],
  gizem: AVATAR_MAP['gizem'],
  mehmet: AVATAR_MAP['mehmet'],
  ozan: AVATAR_MAP['ozan'],
  selin: AVATAR_MAP['selin'],
  zeynep: AVATAR_MAP['zeynep'],
};

const REMOTE_TO_LOCAL: Record<string, string> = {
  'women/1.jpg': 'zeynep',
  'women/5.jpg': 'elif',
  'women/8.jpg': 'ece',
  'women/12.jpg': 'asli',
  'women/14.jpg': 'gizem',
  'women/18.jpg': 'selin',
  'men/1.jpg': 'ahmet',
  'men/5.jpg': 'mehmet',
  'men/8.jpg': 'can',
  'men/12.jpg': 'ozan',
  'men/14.jpg': 'emre',
  'men/18.jpg': 'alp',
};

export function cleanAvatarUrl(url: string): string {
  if (url.startsWith('data:')) return url;
  return url
    .replace(/ı/g, 'i')
    .replace(/İ/g, 'i')
    .replace(/I/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/Ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/Ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/Ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/Ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/Ç/g, 'c')
    .toLowerCase();
}

const UNSPLASH_PHOTO_MAP: Record<string, string> = {
  '1567532939604': 'zeynep',
  '1544005313': 'elif',
  '1438761681033': 'ece',
  '1494790108377': 'asli',
  '1524504388940': 'gizem',
  '1580489944761': 'selin',
  '1500648767791': 'ahmet',
  '1492562080023': 'mehmet',
  '1519085360753': 'can',
  '1537368910025': 'ozan',
  '1534030347209': 'emre',
  '1506794778202': 'alp',
};

export function resolveAvatarUrl(url: any): string {
  // Helper to extract string from Vite import objects
  const extractUrl = (val: any): string => {
    if (typeof val === 'string') return val;
    if (val && typeof val === 'object') {
      if ('default' in val && typeof val.default === 'string') return val.default;
      const stringVal = Object.values(val).find(v => typeof v === 'string');
      if (stringVal) return stringVal as string;
    }
    return '';
  };

  const DEFAULT_AVATAR = AVATAR_MAP['ahmet'] || Object.values(AVATAR_MAP)[0] || '';
  
  if (!url || (typeof url === 'string' && url.trim() === '')) return DEFAULT_AVATAR;

  if (typeof url === 'object') {
    url = extractUrl(url);
    if (!url || (typeof url === 'string' && url.trim() === '')) return DEFAULT_AVATAR;
  }

  if (typeof url !== 'string') {
    return DEFAULT_AVATAR;
  }

  // Handle data URLs (Base64 uploaded avatars and SVG avatars)
  if (url.startsWith('data:')) {
    return url;
  }

  // Exact match in AVATAR_MAP key
  const cleanedKey = url.trim().toLowerCase();
  if (AVATAR_MAP[cleanedKey]) {
    return AVATAR_MAP[cleanedKey];
  }
  if (REALISTIC_UNSPLASH_AVATARS[cleanedKey]) {
    return REALISTIC_UNSPLASH_AVATARS[cleanedKey];
  }

  // Check if it matches a remote avatar pattern
  const remoteMatch = url.match(/(women|men)\/(\d+)\.jpg$/i);
  if (remoteMatch) {
    const key = `${remoteMatch[1].toLowerCase()}/${remoteMatch[2]}.jpg`;
    const name = REMOTE_TO_LOCAL[key];
    if (name && AVATAR_MAP[name]) {
      return AVATAR_MAP[name];
    }
  }

  // Intercept any path/URL that references /avatars/<name> or /assets/<name>
  const assetMatch = url.match(/(?:avatars|assets)\/([a-z_]+)(?:-[a-z0-9_-]+)?\.(?:jpg|jpeg|png|webp|svg)/i);
  if (assetMatch) {
    const avatarName = assetMatch[1].toLowerCase();
    if (AVATAR_MAP[avatarName]) {
      return AVATAR_MAP[avatarName];
    }
  }

  const avatarMatch = url.match(/avatars\/([^/?#.]+)/i);
  if (avatarMatch) {
    const avatarName = avatarMatch[1].toLowerCase();
    if (AVATAR_MAP[avatarName]) {
      return AVATAR_MAP[avatarName];
    }
    return AVATAR_MAP['ahmet'] || DEFAULT_AVATAR;
  }
  
  // Check if it matches an Unsplash photo ID
  for (const [photoId, name] of Object.entries(UNSPLASH_PHOTO_MAP)) {
    if (url.includes(photoId)) {
      return AVATAR_MAP[name] || DEFAULT_AVATAR;
    }
  }

  // Intercept generic Unsplash URLs and map to local avatars
  if (url.includes('unsplash.com')) {
    return DEFAULT_AVATAR;
  }
  
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  let cleaned = cleanAvatarUrl(url);
  
  // Try to find if there is an explicit key match inside the string (e.g. "zeynep")
  const keys = Object.keys(AVATAR_MAP).sort((a, b) => b.length - a.length);
  for (const key of keys) {
    if (cleaned.includes(key)) {
      return AVATAR_MAP[key] || DEFAULT_AVATAR;
    }
  }

  // Handle generic player names like "Oyuncu 1", "Oyuncu 2", "Player 1", etc.
  const numMatch = cleaned.match(/(?:oyuncu|player|bot|kullanici)\s*(\d+)/i);
  if (numMatch) {
    const playerNum = parseInt(numMatch[1], 10);
    const predefinedKeys = ['ahmet', 'zeynep', 'can', 'elif', 'mehmet', 'ece', 'ozan', 'asli', 'emre', 'gizem', 'alp', 'selin'];
    const chosenKey = predefinedKeys[(playerNum - 1) % predefinedKeys.length];
    return AVATAR_MAP[chosenKey] || DEFAULT_AVATAR;
  }

  // Deterministic fallback based on string hash for any other custom name
  let hash = 0;
  for (let i = 0; i < cleaned.length; i++) {
    hash = cleaned.charCodeAt(i) + ((hash << 5) - hash);
  }
  const allKeys = ALL_AVATAR_KEYS;
  const hashKey = allKeys[Math.abs(hash) % allKeys.length];
  return AVATAR_MAP[hashKey] || DEFAULT_AVATAR;
}

/**
 * Compresses uploaded custom user avatar images to a light, highly optimized
 * 256x256 Base64 JPEG Data URL to keep PWA memory, localStorage, and P2P payload tiny.
 */
export function compressAvatarFile(file: File, maxDim = 256, quality = 0.85): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxDim) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          }
        } else {
          if (height > maxDim) {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        } else {
          resolve(e.target?.result as string || '');
        }
      };
      img.onerror = () => {
        resolve(e.target?.result as string || '');
      };
      img.src = e.target?.result as string;
    };
    reader.onerror = () => resolve('');
    reader.readAsDataURL(file);
  });
}

